import { useEffect, useMemo, useState } from 'react'
import { FileText, Folder, Lock, Plus, Settings2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { fetchWithAuth } from '@/lib/api'
import { useWorkspace } from '@/providers/workspace-provider'
import { deskPathForMember } from '@/lib/workspace-desk-path'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

interface RecentDoc {
  id: string
  title: string
  filePath: string
  updatedAtRaw?: string
}

interface DeskLandingDashboardProps {
  workspaceId: string
  deskSlug: string
  onDocumentClick?: (documentId: string, event?: React.MouseEvent) => void
  onFolderClick?: (
    folderPath: string,
    skipTabManagementOrEvent?: boolean | React.MouseEvent,
  ) => void
}

function relativeUnderDesk(filePath: string, deskPath: string): string {
  const norm = filePath.replace(/\/$/, '')
  const root = deskPath.replace(/\/$/, '')
  if (norm === root) return ''
  if (norm.startsWith(root + '/')) return norm.slice(root.length + 1)
  return filePath
}

export function DeskLandingDashboard({
  workspaceId,
  deskSlug,
  onDocumentClick,
  onFolderClick,
}: DeskLandingDashboardProps) {
  const { workspaces } = useWorkspace()
  const ws = workspaces.find((w) => w.id === workspaceId)
  const deskPath = useMemo(
    () => (ws ? deskPathForMember(ws, deskSlug) : ''),
    [ws, deskSlug],
  )

  const [recentDocs, setRecentDocs] = useState<RecentDoc[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!workspaceId || !deskPath) {
      setLoading(false)
      setRecentDocs([])
      return
    }
    setLoading(true)
    fetchWithAuth(
      `/api/documents/recent?limit=8&sortBy=edited&workspaceId=${encodeURIComponent(workspaceId)}`,
    )
      .then((r) => r.json())
      .then((data: { documents?: RecentDoc[] }) => {
        const docs = data.documents ?? []
        const prefix = deskPath.replace(/\/$/, '') + '/'
        setRecentDocs(
          docs.filter(
            (d) =>
              d.filePath === deskPath.replace(/\/$/, '') ||
              d.filePath.startsWith(prefix),
          ),
        )
      })
      .catch(() => setRecentDocs([]))
      .finally(() => setLoading(false))
  }, [workspaceId, deskPath])

  const openDeskFolder = (rel: string) => {
    if (!deskPath || !onFolderClick) return
    const path = rel ? `${deskPath.replace(/\/$/, '')}/${rel}` : deskPath
    onFolderClick(path)
  }

  const triggerNewNote = () => {
    if (!deskPath) return
    window.dispatchEvent(
      new CustomEvent('create-file-at-path', {
        detail: { folderPath: deskPath, workspaceId },
      }),
    )
  }

  const deskLabel = deskSlug

  return (
    <div
      className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto"
      data-testid={testIds.deskLanding.root}
    >
      <div className="border-b border-border bg-muted/30 px-8 py-6">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {ws?.name ?? 'Workspace'} · Workspace Desk
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          My documents
          <span className="ml-2 text-base font-normal text-muted-foreground">
            {deskLabel}
          </span>
        </h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          Your desk in this workspace - drafts, private notes, and role soul. Not App Home.
        </p>
        <p className="mt-1 font-mono text-[11px] text-muted-foreground/80">
          users/{deskSlug}/
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            onClick={triggerNewNote}
            disabled={!deskPath}
            data-testid={testIds.deskLanding.newNote}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New note on desk
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => openDeskFolder('.soul/preferences')}
            disabled={!deskPath}
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            Open .soul/preferences
          </Button>
        </div>
      </div>

      <div className="grid gap-6 px-8 py-6 lg:grid-cols-2">
        <section className="rounded-lg border border-border p-4">
          <div className="mb-3 flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold">Private</h2>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            Owner + workspace admins only. Sensitive drafts land here when you need privacy.
          </p>
          <button
            type="button"
            className="text-sm text-primary underline-offset-2 hover:underline"
            onClick={() => openDeskFolder('private')}
            data-testid={testIds.deskLanding.openPrivate}
          >
            Open private/
          </button>
        </section>

        <section className="rounded-lg border border-border p-4">
          <div className="mb-3 flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            <h2 className="text-sm font-semibold">Desk shortcuts</h2>
          </div>
          <ul className="space-y-2 text-sm">
            {(
              [
                { rel: 'meetings', label: 'meetings/', locked: false },
                { rel: 'notes', label: 'notes/', locked: false },
                { rel: '.soul', label: '.soul/ (agent identity here)', locked: true },
              ] as const
            ).map((item) => (
              <li key={item.rel}>
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-2 text-left text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => openDeskFolder(item.rel)}
                >
                  {item.locked ? (
                    <Lock className="h-3.5 w-3.5" />
                  ) : (
                    <Folder className="h-3.5 w-3.5" />
                  )}
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-lg border border-border p-4 lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <h2 className="text-sm font-semibold">Recent on this desk</h2>
          </div>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : recentDocs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No recent documents under this desk yet. Create a note or open private/ to get started.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {recentDocs.map((row) => (
                <li key={row.id} className="flex items-center justify-between py-2.5 text-sm first:pt-0">
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left hover:underline"
                    onClick={(e) => onDocumentClick?.(row.id, e)}
                  >
                    <div className="font-medium truncate">{row.title}</div>
                    <div className="font-mono text-[11px] text-muted-foreground truncate">
                      {relativeUnderDesk(row.filePath, deskPath)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
